/**
 * shared/nhanXetDeck.js: khuôn dữ liệu chung cho tính năng nhận xét pitch deck.
 *
 * Dùng ở ba nơi, nên phải là một nguồn duy nhất:
 *   - trang founder /nhan-xet-deck (gửi yêu cầu, đọc kết quả)
 *   - trang quản trị /admin/nhan-xet-deck (biên tập bản nháp AI)
 *   - Cloud Function (chuẩn hoá câu trả lời của AI trước khi lưu)
 * Build chép tệp này sang functions/chung/ vì Cloud Functions chỉ tải lên thư
 * mục functions/.
 *
 * Hàm thuần, không đụng tới Firebase hay `document`.
 */
import { suaDau } from './suaDau.js';
import { DECK_CHECKLIST } from './rubrics/deckChecklist.js';

/** Mỗi tài khoản được AI nhận xét tối đa GIOI_HAN_TUAN lần trong SO_NGAY_HAN_MUC
 *  ngày gần nhất (cửa sổ trượt). Mỗi lần AI đọc tốn tiền thật. Lượt bị từ chối
 *  hoặc AI lỗi (status tu_choi) không tính. */
export const GIOI_HAN_TUAN = 2;
export const SO_NGAY_HAN_MUC = 7;
/** Founder chờ bao lâu: hứa trên trang và trong email. AI thường xong trong 3 đến 8 phút. */
export const THOI_GIAN_CHO = '15 đến 30 phút';
export const TRAN_MB = 20;
export const NGAY_GIU_DECK = 90;

export const GIAI_DOAN = ['Ý tưởng', 'Có sản phẩm thử', 'Có doanh thu đầu tiên', 'Đang tăng trưởng'];
export const VONG = ['Angel', 'Pre-seed', 'Seed', 'Chưa chắc'];

export const TRANG_THAI = {
  moi: { nhan: 'Đã nhận', moTa: 'Deck đã vào hàng chờ.' },
  dang_xem: { nhan: 'Đang nhận xét', moTa: 'AI đang đọc và nhận xét deck của bạn.' },
  da_gui: { nhan: 'Đã có nhận xét', moTa: 'Nhận xét đã sẵn sàng.' },
  tu_choi: { nhan: 'Không nhận xét được', moTa: 'Xem lý do bên dưới.' },
};

export const MUC_DO = {
  'chua-san-sang': 'Chưa nên gửi quỹ',
  'can-sua-nhieu': 'Cần sửa nhiều trước khi gửi',
  'gan-san-sang': 'Gần sẵn sàng, sửa vài chỗ',
  'san-sang': 'Sẵn sàng gửi',
};

export const DANH_GIA_SLIDE = {
  tot: 'Ổn',
  'can-sua': 'Cần sửa',
  'nghiem-trong': 'Nghiêm trọng',
  'nen-bo': 'Nên bỏ hoặc gộp',
};

/** Việc ưu tiên của lượt trước đã sửa tới đâu (AI chấm ở lượt sau). */
export const KET_QUA_SUA = { 'da-sua': 'Đã sửa', 'mot-phan': 'Sửa một phần', 'chua-sua': 'Chưa sửa' };

/** Mức ưu tiên của một việc cần sửa. */
export const MUC_UU_TIEN = { cao: 'Ưu tiên cao', vua: 'Nên sửa' };

/**
 * Checklist pitch deck của chính website (công cụ /cong-cu/pitch-deck-10-slide):
 * AI đánh dấu từng tiêu chí, nên con số "đạt x/16" có căn cứ và trùng với cái
 * founder tự chấm được trên site.
 */
export const TIEU_CHI = DECK_CHECKLIST.axes.flatMap((a) => a.criteria.map((c) => ({ ma: c.key, ten: c.label })));
export const KET_QUA_TIEU_CHI = { dat: 'Đạt', 'mot-phan': 'Một phần', chua: 'Chưa có' };

/** Đếm số slide theo mức để hiện ở bảng tổng quan. */
export function demMuc(slides = []) {
  const d = { nghiemTrong: 0, canSua: 0, on: 0 };
  for (const s of slides) {
    if (s.danhGia === 'nghiem-trong') d.nghiemTrong++;
    else if (s.danhGia === 'tot') d.on++;
    else d.canSua++;
  }
  return d;
}

/** Bảy trục chấm điểm, theo đúng thứ tự nhà đầu tư đọc một deck. */
export const TRUC = [
  ['van-de', 'Vấn đề và giải pháp'],
  ['thi-truong', 'Thị trường và thời điểm'],
  ['bang-chung', 'Bằng chứng (traction)'],
  ['mo-hinh', 'Mô hình kinh doanh và cạnh tranh'],
  ['doi-ngu', 'Đội ngũ'],
  ['tai-chinh', 'Tài chính và số vốn gọi'],
  ['trinh-bay', 'Cách trình bày'],
];

/** Ngoặc kép kiểu Pháp và ngoặc cong đổi sang ngoặc kép thường "…" cho tự nhiên. */
export function doiNgoac(str) {
  return String(str ?? '')
    .replace(/«\s*/g, '"').replace(/\s*»/g, '"')
    .replace(/[“”„]/g, '"');
}
const cat = (v, max) => doiNgoac(suaDau(String(v ?? '').trim())).slice(0, max);
const ds = (v, max, toiDa) => (Array.isArray(v) ? v : [])
  .map((x) => cat(x, max)).filter(Boolean).slice(0, toiDa);
const so = (v, min, max) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : null;
};

/**
 * Chuẩn hoá một bản nhận xét: từ AI, hoặc từ form quản trị. Cắt độ dài, bỏ
 * giá trị lạ, sửa dấu câu theo quy ước của site, và chỉ giữ đường dẫn đọc
 * thêm có thật (AI có thể bịa đường dẫn).
 */
export function chuanHoaNhanXet(raw = {}, duongDanHopLe = null) {
  const hopLe = duongDanHopLe ? new Map(duongDanHopLe.map((x) => [x.path || x, x.title || ''])) : null;
  const trucVao = new Map((Array.isArray(raw.truc) ? raw.truc : []).map((t) => [t?.ma, t]));
  return {
    tomTat: cat(raw.tomTat, 2000),
    diemTong: so(raw.diemTong, 0, 100),
    mucDo: raw.mucDo in MUC_DO ? raw.mucDo : 'can-sua-nhieu',
    truc: TRUC.map(([ma, ten]) => {
      const t = trucVao.get(ma) || {};
      return { ma, ten, diem: so(t.diem, 1, 5), nhanXet: cat(t.nhanXet, 800) };
    }),
    uuTien: (Array.isArray(raw.uuTien) ? raw.uuTien : []).slice(0, 7)
      .map((x) => ({ viec: cat(x?.viec, 300), viSao: cat(x?.viSao, 600), muc: x?.muc in MUC_UU_TIEN ? x.muc : 'cao', slide: cat(x?.slide, 30) }))
      .filter((x) => x.viec),
    diemDung: (Array.isArray(raw.diemDung) ? raw.diemDung : []).slice(0, 4)
      .map((x) => ({ chuDe: cat(x?.chuDe, 120), lyDo: cat(x?.lyDo, 500), slide: cat(x?.slide, 30) })).filter((x) => x.chuDe),
    diemManh: ds(raw.diemManh, 400, 4),
    slides: (Array.isArray(raw.slides) ? raw.slides : []).slice(0, 60).map((s, i) => ({
      so: so(s?.so, 1, 999) ?? i + 1,
      tieuDe: cat(s?.tieuDe, 160),
      danhGia: s?.danhGia in DANH_GIA_SLIDE ? s.danhGia : 'can-sua',
      diem: (Array.isArray(s?.diem) ? s.diem : []).slice(0, 6).map((d) => ({
        trich: cat(d?.trich, 300), viSao: cat(d?.viSao, 600), suaGi: cat(d?.suaGi, 400), cachSua: cat(d?.cachSua, 900),
      })).filter((d) => d.viSao || d.suaGi || d.cachSua),
      vanDe: ds(s?.vanDe, 500, 6),
      goiY: ds(s?.goiY, 600, 6),
      nhanXetNgan: cat(s?.nhanXetNgan, 240),
      hienTai: cat(s?.hienTai, 800),
      vietLai: cat(s?.vietLai, 1200),
    })),
    checklist: (() => {
      const vao = new Map((Array.isArray(raw.checklist) ? raw.checklist : []).map((x) => [x?.ma, x]));
      if (!vao.size) return [];
      return TIEU_CHI.map(({ ma, ten }) => {
        const x = vao.get(ma) || {};
        return { ma, ten, ketQua: x.ketQua in KET_QUA_TIEU_CHI ? x.ketQua : 'chua', ghiChu: cat(x.ghiChu, 300) };
      });
    })(),
    thieu: (Array.isArray(raw.thieu) ? raw.thieu : []).slice(0, 10)
      .map((x) => ({ noiDung: cat(x?.noiDung, 200), viSao: cat(x?.viSao, 600) })).filter((x) => x.noiDung),
    cauHoi: ds(raw.cauHoi, 400, 10),
    docThem: (Array.isArray(raw.docThem) ? raw.docThem : []).slice(0, 6)
      .map((x) => {
        const path = String(x?.path || '').trim();
        return { path, ten: cat(hopLe?.get(path) || x?.ten, 200), lyDo: cat(x?.lyDo, 300) };
      })
      .filter((x) => /^\/[a-z0-9/-]+$/.test(x.path) && (!hopLe || hopLe.has(x.path))),
    soVoiLanTruoc: (() => {
      const v = raw.soVoiLanTruoc || {};
      const viec = (Array.isArray(v.viec) ? v.viec : []).slice(0, 10)
        .map((x) => ({ viec: cat(x?.viec, 300), ketQua: x?.ketQua in KET_QUA_SUA ? x.ketQua : 'chua-sua', ghiChu: cat(x?.ghiChu, 400) }))
        .filter((x) => x.viec);
      const nhanXet = cat(v.nhanXet, 1200);
      return nhanXet || viec.length ? { nhanXet, viec } : null;
    })(),
    loiNhan: cat(raw.loiNhan, 3000),
    ghiChuNoiBo: cat(raw.ghiChuNoiBo, 3000),
  };
}

/** Bản gửi founder: bỏ ghi chú nội bộ. */
export function banChoFounder(nx) {
  const { ghiChuNoiBo, ...con } = chuanHoaNhanXet(nx);
  return con;
}

/**
 * Lấy JSON từ câu trả lời của AI. Chấp nhận cả khi AI bọc trong ```json, hoặc
 * viết thêm vài câu trước và sau. Trả về null nếu không đọc được.
 */
export function tachJson(text) {
  const s = String(text || '');
  const khoi = s.match(/```(?:json)?\s*([\s\S]*?)```/);
  const ung = [khoi?.[1], s, s.slice(s.indexOf('{'), s.lastIndexOf('}') + 1)];
  for (const u of ung) {
    if (!u) continue;
    try { const o = JSON.parse(u.trim()); if (o && typeof o === 'object') return o; } catch { /* thử cách sau */ }
  }
  return null;
}

/** Chuẩn hoá form founder gửi lên. */
export function chuanHoaYeuCau(raw = {}) {
  const c = (v, max) => String(v ?? '').trim().slice(0, max);
  return {
    congTy: c(raw.congTy, 160),
    nganh: c(raw.nganh, 120),
    giaiDoan: GIAI_DOAN.includes(raw.giaiDoan) ? raw.giaiDoan : GIAI_DOAN[0],
    vong: VONG.includes(raw.vong) ? raw.vong : VONG[3],
    soVon: c(raw.soVon, 120),
    canGopY: c(raw.canGopY, 1500),
    dongY: Boolean(raw.dongY),
  };
}

export function kiemTraYeuCau(y, tep) {
  const loi = [];
  if (!y.congTy) loi.push('Cho biết tên công ty hoặc dự án.');
  if (!tep) loi.push('Chọn tệp pitch deck dạng PDF.');
  else {
    if (tep.type !== 'application/pdf' && !/\.pdf$/i.test(tep.name || ''))
      loi.push('Deck cần ở dạng PDF. Trong PowerPoint, Keynote hay Canva, chọn Xuất (Export) sang PDF.');
    if (tep.size > TRAN_MB * 1024 * 1024) loi.push(`Tệp lớn hơn ${TRAN_MB} MB. Hãy nén ảnh hoặc xuất PDF ở chất lượng thấp hơn.`);
  }
  if (!y.dongY) loi.push('Cần đồng ý với cách chúng tôi xử lý deck.');
  return loi;
}

// ── Lượt nhận xét ──────────────────────────────────────────────────────
// Mỗi lần founder gửi deck là một yêu cầu riêng (deck và nhận xét giữ riêng).
// Các yêu cầu cùng tài khoản, cùng tên công ty là các lượt của một deck: lượt 1
// là bản đầu tiên, lượt 2 là bản đã sửa theo nhận xét lượt 1, và cứ thế.

/** Tên công ty để so: bỏ dấu, bỏ chữ loại hình doanh nghiệp, chỉ giữ chữ và số. */
export function khoaCongTy(ten) {
  return String(ten || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/gi, 'd').toLowerCase()
    .replace(/\b(cong ty|cty|tnhh|co phan|cp|jsc|ltd|llc|inc|corp|startup|du an)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '');
}

const moc = (y) => (y?.createdAt instanceof Date ? y.createdAt.getTime() : Number(y?.createdAt?.toMillis?.() ?? y?.createdAt ?? 0));

/**
 * Các lượt của cùng deck với yêu cầu `id`, cũ trước mới sau. Mỗi phần tử là
 * yêu cầu gốc kèm số lượt. ds là mọi yêu cầu của cùng tài khoản.
 */
export function cacLuot(ds, id) {
  const goc = (ds || []).find((y) => y.id === id);
  if (!goc) return [];
  const k = khoaCongTy(goc.congTy);
  return (ds || []).filter((y) => y.uid === goc.uid && k && khoaCongTy(y.congTy) === k)
    .sort((a, b) => moc(a) - moc(b))
    .map((y, i) => ({ ...y, luot: i + 1 }));
}

/** Lượt ngay trước có kết quả đã gửi, hoặc null. */
export function luotTruocCoKetQua(ds, id) {
  const luot = cacLuot(ds, id);
  const i = luot.findIndex((y) => y.id === id);
  for (let j = i - 1; j >= 0; j -= 1) if (luot[j].status === 'da_gui' && luot[j].ketQua) return luot[j];
  return null;
}

/** So hai bản nhận xét: điểm, từng trục, số slide theo mức, từng slide theo số. */
export function soSanhLuot(truoc, sau) {
  if (!truoc || !sau) return null;
  const tMap = new Map((truoc.truc || []).map((t) => [t.ma, t.diem]));
  const sMap = new Map((sau.slides || []).map((s) => [s.so, s]));
  const tsMap = new Map((truoc.slides || []).map((s) => [s.so, s]));
  const soSlide = [...new Set([...tsMap.keys(), ...sMap.keys()])].sort((a, b) => a - b);
  return {
    diem: { truoc: truoc.diemTong ?? null, sau: sau.diemTong ?? null },
    mucDo: { truoc: truoc.mucDo, sau: sau.mucDo },
    truc: (sau.truc || []).map((t) => ({ ma: t.ma, ten: t.ten, truoc: tMap.get(t.ma) ?? null, sau: t.diem ?? null })),
    dem: { truoc: demMuc(truoc.slides), sau: demMuc(sau.slides) },
    slides: soSlide.map((so) => ({ so, truoc: tsMap.get(so) || null, sau: sMap.get(so) || null })),
  };
}

/** Bản tóm lượt trước gửi cho AI ở lượt sau, để AI chấm việc nào đã sửa. */
export function tomTatChoLuotSau(ketQua) {
  if (!ketQua) return '';
  const nx = chuanHoaNhanXet(ketQua);
  const dong = [
    `Điểm lượt trước: ${nx.diemTong ?? 'chưa chấm'}/100, mức: ${MUC_DO[nx.mucDo]}.`,
    'Việc ưu tiên đã đề nghị founder sửa:',
    ...nx.uuTien.map((x, i) => `${i + 1}. ${x.viec}${x.slide ? ` (slide ${x.slide})` : ''}`),
    'Nhận xét ngắn từng slide lượt trước:',
    ...nx.slides.map((s) => `Slide ${s.so}${s.tieuDe ? ` (${s.tieuDe})` : ''}: ${DANH_GIA_SLIDE[s.danhGia]}. ${s.nhanXetNgan}`),
  ];
  return dong.join('\n');
}

// ── Hình slide ─────────────────────────────────────────────────────────

/** Firestore giới hạn 1 MB mỗi tài liệu: cắt bớt mẩu chữ nếu deck quá dày chữ. */
export const TRAN_BYTE_HINH = 600 * 1024;

/**
 * Chuẩn hoá dữ liệu hình slide trước khi lưu hoặc chép sang bản founder đọc.
 * Mỗi trang: { so, tiLe, anh (đường dẫn Storage), chu: [{ s, x, y, w, h }] }.
 */
export function chuanHoaHinh(trang) {
  const so = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v) * 10000) / 10000 : 0);
  let ra = (Array.isArray(trang) ? trang : []).slice(0, 60).map((t, i) => ({
    so: Math.round(Number(t?.so)) || i + 1,
    tiLe: so(t?.tiLe) || 0.5625,
    anh: String(t?.anh || '').slice(0, 300),
    chu: (Array.isArray(t?.chu) ? t.chu : []).slice(0, 300).map((m) => ({ s: String(m?.s || '').slice(0, 300), x: so(m?.x), y: so(m?.y), w: so(m?.w), h: so(m?.h) })),
  })).filter((t) => t.anh);
  // Quá trần thì giảm dần số mẩu chữ mỗi trang.
  for (let tran = 200; JSON.stringify(ra).length > TRAN_BYTE_HINH && tran >= 20; tran = Math.floor(tran * 0.7)) {
    ra = ra.map((t) => ({ ...t, chu: t.chu.slice(0, tran) }));
  }
  return ra;
}

// ── Hạn mức ────────────────────────────────────────────────────────────

const ngayMs = 24 * 60 * 60 * 1000;

/**
 * Hạn mức của một tài khoản tại thời điểm `bayGio` (ms). ds là mọi yêu cầu của
 * tài khoản đó; `boId` là yêu cầu đang xét (không tự đếm chính nó).
 * Trả về { daDung, conLai, moLaiLuc }: moLaiLuc là lúc lượt cũ nhất trong cửa
 * sổ hết tính (ms), null nếu còn lượt.
 */
export function hanMuc(ds, bayGio = Date.now(), boId = null) {
  const tu = bayGio - SO_NGAY_HAN_MUC * ngayMs;
  const tinh = (ds || [])
    .filter((y) => y && y.id !== boId && y.status !== 'tu_choi')
    .map(moc).filter((t) => t > tu && t <= bayGio).sort((a, b) => a - b);
  const conLai = Math.max(0, GIOI_HAN_TUAN - tinh.length);
  const moLaiLuc = conLai > 0 ? null : tinh[tinh.length - GIOI_HAN_TUAN] + SO_NGAY_HAN_MUC * ngayMs;
  return { daDung: tinh.length, conLai, moLaiLuc };
}
